import {
  StorageLayer,
  StorageOptions,
  StorageResult,
  StorageStatus,
  StorageError,
  StorageErrorCode,
  type DocumentWriteOptions,
} from "./types";
import { localStorageManager } from "./local-storage";
import { indexedDBManager } from "./indexed-db";
import { logger } from "../../utils/logger";
import { isProjectDocumentNamespaceKey } from "../projectStorageMigration";
import { isRecoverySlotKey } from "../recoveryJournal";
import {
  reportWriteRefused,
  reportStorageFailure,
  reportSafetyCopyFailure,
  reportDocumentSaved,
} from "../writeRefusals";

/**
 * Coordinates storage operations across different storage types
 */
export class StorageCoordinator {
  private readonly LOCAL_STORAGE_SIZE_LIMIT = 2 * 1024 * 1024; // 2MB
  private storages: Map<string, StorageLayer> = new Map();

  constructor() {
    // Initialize storages after construction to avoid type issues
    this.storages.set("localStorage", localStorageManager);
    this.storages.set("indexedDB", indexedDBManager);
  }

  /**
   * Save data to appropriate storage
   */
  async save<T>(
    key: string,
    data: T,
    _options?: StorageOptions,
    write?: DocumentWriteOptions,
  ): Promise<StorageResult<void>> {
    // Bombproof storage foundation, step 5 (board 12-imgstore; approval-gate
    // §6.2/§7): `project_*` DOCUMENTS live on the single IndexedDB tier —
    // durability keyed on the REAL put outcome, never a silent localStorage
    // document write (that tier is pointers only).
    // WR-685: and every one of them is GUARDED — `write` carries what the writer
    // declares; a document write without an owner is refused (`unowned`).
    if (isProjectDocumentNamespaceKey(key)) {
      return this.saveDocument(key, data, write);
    }

    try {
      const size = this.calculateSize(data);

      // For small data, try to save to both storages
      if (size <= this.LOCAL_STORAGE_SIZE_LIMIT) {
        const status = await this.getStatus();
        const results = await Promise.all(
          Array.from(this.storages.values())
            .filter(
              (storage) =>
                status[
                  storage === this.storages.get("localStorage")
                    ? "localStorage"
                    : "indexedDB"
                ].available,
            )
            .map((storage) => storage.save(key, data, _options)),
        );

        // Return success if any storage succeeded
        if (results.some((result) => result.success)) {
          return { success: true };
        }

        // If all failed, return the first error (guard against empty results)
        if (results.length === 0) {
          return {
            success: false,
            error: new StorageError(
              "No storage backends available",
              StorageErrorCode.NOT_AVAILABLE,
            ),
          };
        }
        return results[0];
      }

      // For larger data, save to IndexedDB only
      const selectedStorage = await this.selectStorage(data, _options);
      const result = await selectedStorage.save(key, data, _options);

      // Clean up stale localStorage copy to prevent load() from returning old data.
      // This handles the case where data grew from <2MB (saved to both) to >2MB (IndexedDB only).
      if (result.success) {
        const localStorage = this.storages.get("localStorage");
        if (localStorage) {
          try {
            await localStorage.delete(key, _options);
          } catch {
            // Non-critical — stale data will be cleaned up next time
          }
        }
      }

      return result;
    } catch (error) {
      logger.error("Failed to save data", {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      return {
        success: false,
        error: new StorageError(
          "Failed to save data",
          StorageErrorCode.OPERATION_FAILED,
          { error },
        ),
      };
    }
  }

  /**
   * Single-tier DOCUMENT save (approval-gate §7): IndexedDB is the sole
   * document store. The save is durable only when the real IndexedDB put
   * succeeds — one retry on a transient failure, then a LOUD failure result.
   * There is NO localStorage document fallback: a silent fallback would
   * resurrect the split-brain/stale-shadow class this foundation removes.
   * On success, any stale legacy localStorage copy of the key is deleted so
   * the (pre-flip) LS-first loader can never serve a stale shadow (F-SHADOW,
   * RESHAPE-4). This also folds the offline queue (approval-gate §6.10): a
   * replayed document-keyed operation re-enters here and lands single-tier.
   */
  private async saveDocument<T>(
    key: string,
    data: T,
    write: DocumentWriteOptions = {},
  ): Promise<StorageResult<void>> {
    // WR-685 (D3): the ONE document write seam. The write is decided by
    // `decideDocumentWrite` INSIDE its own IndexedDB transaction
    // (`indexedDBManager.guardedSave`). A REFUSAL is not a storage failure: it is
    // reported once to the refusal channel and returned — never retried, never
    // logged as a failed save. Real failures keep the retry-once below.
    const guarded = () => indexedDBManager.guardedSave(key, data, write);
    const refused = (r: StorageResult<void>) =>
      r.error?.code === StorageErrorCode.WRITE_REFUSED;
    // s10 (CTO 65-108): the recovery journal could not keep its safety copy for a reason
    // other than storage being full — a defect, not storage. Never retried, never reported
    // as a storage failure; told once through its own outcome.
    const safetyCopyFailed = (r: StorageResult<void>) =>
      r.error?.code === StorageErrorCode.SAFETY_COPY_FAILED;

    let result: StorageResult<void>;
    try {
      result = await guarded();
    } catch (error) {
      result = {
        success: false,
        error: new StorageError(
          "Document save failed",
          StorageErrorCode.OPERATION_FAILED,
          { error },
        ),
      };
    }

    if (refused(result)) {
      reportWriteRefused(
        write.guard?.projectId ?? "",
        result.metadata?.refusal ?? "unowned",
        key,
      );
      return result;
    }
    if (safetyCopyFailed(result)) {
      reportSafetyCopyFailure(write.guard?.projectId ?? "", key);
      return result;
    }

    if (!result.success) {
      logger.error(
        "StorageCoordinator: document save failed on IndexedDB — retrying once",
        { error: result.error, data: { key } },
      );
      try {
        result = await guarded();
      } catch (error) {
        result = {
          success: false,
          error: new StorageError(
            "Document save failed after retry",
            StorageErrorCode.OPERATION_FAILED,
            { error },
          ),
        };
      }
      if (refused(result)) {
        reportWriteRefused(
          write.guard?.projectId ?? "",
          result.metadata?.refusal ?? "unowned",
          key,
        );
        return result;
      }
      if (safetyCopyFailed(result)) {
        reportSafetyCopyFailure(write.guard?.projectId ?? "", key);
        return result;
      }
    }

    if (!result.success) {
      // LOUD: the caller must see this. Never degrade to a localStorage
      // document write — surfacing the failure is the bombproof behavior.
      logger.error(
        "StorageCoordinator: DOCUMENT SAVE FAILED after retry — IndexedDB rejected the put; no localStorage fallback exists for documents",
        { error: result.error, data: { key } },
      );
      // WR-687: TELL the user (the one messenger, once per failure run). Tell only —
      // no fallback tier, no retry change. A refusal returned above, so one write
      // yields exactly one outcome.
      reportStorageFailure(write.guard?.projectId ?? "", key);
      return result;
    }
    reportDocumentSaved(write.guard?.projectId ?? "");

    // Migration-window hygiene: a legacy localStorage copy of this document is
    // now stale by construction — remove it (best-effort).
    try {
      await this.storages.get("localStorage")!.delete(key);
    } catch {
      // Non-critical: the boot migration / next save re-attempts.
    }

    return result;
  }

  /**
   * Load data from appropriate storage
   */
  async load<T>(
    key: string,
    _options?: StorageOptions,
  ): Promise<StorageResult<T>> {
    // Bombproof storage foundation, step 6 (board 12-imgstore; approval-gate
    // §7): `project_*` DOCUMENT loads are IndexedDB-first-authoritative.
    if (isProjectDocumentNamespaceKey(key)) {
      return this.loadDocument<T>(key, _options);
    }

    // Try each storage in order until data is found
    let everyTierMissed = true;
    for (const storage of this.storages.values()) {
      const result = await storage.load<T>(key, _options);
      if (result.success) {
        return result;
      }
      // Continue if data not found, but stop on other errors
      if (result.error?.code !== StorageErrorCode.NOT_AVAILABLE) {
        return result;
      }
      if (result.notFound !== true) everyTierMissed = false;
    }

    return {
      success: false,
      error: new StorageError(
        "Data not found in any storage",
        StorageErrorCode.NOT_AVAILABLE,
      ),
      ...(everyTierMissed && { notFound: true }),
    };
  }

  /**
   * Single-tier DOCUMENT load (approval-gate §7): IndexedDB — the sole
   * document tier — is read FIRST and is authoritative when the tiers
   * diverge (the write policy above deletes stale localStorage shadows, and
   * the boot migration reconciles a fresher legacy localStorage copy INTO
   * IndexedDB before the app's document reads — NA-B1″). localStorage is
   * consulted ONLY as the not-yet-migrated legacy fallback while the
   * migration window is open; in steady state it holds no documents and a
   * miss on IndexedDB is a genuine miss. On a dead IndexedDB tier the legacy
   * fallback keeps a not-yet-migrated document reachable; otherwise the
   * authoritative tier's result (including its failure) is returned.
   */
  private async loadDocument<T>(
    key: string,
    _options?: StorageOptions,
  ): Promise<StorageResult<T>> {
    const idb = this.storages.get("indexedDB")!;

    let idbResult: StorageResult<T>;
    try {
      idbResult = await idb.load<T>(key, _options);
    } catch (error) {
      idbResult = {
        success: false,
        error: new StorageError(
          "Document load failed on IndexedDB",
          StorageErrorCode.OPERATION_FAILED,
          { error },
        ),
      };
    }
    if (idbResult.success) {
      return idbResult;
    }

    // Migration-window legacy fallback: a document that has not yet been
    // migrated off localStorage is still served from there.
    let lsMissed = false;
    try {
      const lsResult = await this.storages
        .get("localStorage")!
        .load<T>(key, _options);
      if (lsResult.success) {
        return lsResult;
      }
      lsMissed = lsResult.notFound === true;
    } catch {
      // localStorage unavailable — fall through to the authoritative result.
    }

    // A6: absent only if BOTH tiers genuinely missed; a failed fallback read is
    // not an absence, even though the IndexedDB miss is what gets returned.
    if (idbResult.notFound === true && !lsMissed) {
      const { notFound: _dropped, ...withoutNotFound } = idbResult;
      return withoutNotFound;
    }
    return idbResult;
  }

  /**
   * Delete data from all storages
   */
  async delete(
    key: string,
    _options?: StorageOptions,
  ): Promise<StorageResult<void>> {
    // s10 D12 (FR-P7): a recovery slot key is refused BEFORE any tier is touched — the
    // localStorage legacy copy is a backup copy too (P-RING (ii)), so a per-tier refusal
    // (IndexedDB only) would still lose it. Recovery keys leave only through
    // `indexedDBManager.deleteRecoveryKeys`, the proven tier move, or a project sweep.
    if (isRecoverySlotKey(key)) {
      console.error(
        `[RECOVERY-JOURNAL] refused: storageCoordinator.delete of recovery key ${key}`,
      );
      return {
        success: false,
        error: new StorageError(
          "Recovery key is protected",
          StorageErrorCode.RECOVERY_KEY_PROTECTED,
          { key },
        ),
      };
    }

    const results = await Promise.all(
      Array.from(this.storages.values()).map((storage) =>
        storage.delete(key, _options),
      ),
    );

    // Check if any operation succeeded
    const success = results.some((result) => result.success);
    if (success) {
      return { success: true };
    }

    // Return the first error if all failed
    return results[0];
  }

  /**
   * Clear all storages
   */
  async clear(_options?: StorageOptions): Promise<StorageResult<void>> {
    const results = await Promise.all(
      Array.from(this.storages.values()).map((storage) =>
        storage.clear(_options),
      ),
    );

    // Check if any operation succeeded
    const success = results.some((result) => result.success);
    if (success) {
      return { success: true };
    }

    // Return the first error if all failed
    return results[0];
  }

  /**
   * Get status of all storages
   */
  async getStatus(): Promise<Record<string, StorageStatus>> {
    const status: Record<string, StorageStatus> = {};

    for (const [name, storage] of this.storages) {
      status[name] = await storage.getStatus();
    }

    return status;
  }

  /**
   * Move data between storages
   */
  async moveData(
    key: string,
    fromStorage: string,
    toStorage: string,
    _options?: StorageOptions,
  ): Promise<StorageResult<void>> {
    const source = this.storages.get(fromStorage);
    const target = this.storages.get(toStorage);

    if (!source || !target) {
      return {
        success: false,
        error: new StorageError(
          "Invalid storage specified",
          StorageErrorCode.INVALID_DATA,
        ),
      };
    }

    try {
      // Load from source with explicit unknown type
      const loadResult = await source.load<unknown>(key, _options);
      if (!loadResult.success) {
        return {
          success: false,
          error: loadResult.error,
        };
      }

      // Save to target with explicit void type
      const saveResult = await target.save<unknown>(
        key,
        loadResult.data,
        _options,
      );
      if (!saveResult.success) {
        return saveResult;
      }

      // Delete from source
      return source.delete(key, _options);
    } catch (error) {
      return {
        success: false,
        error: new StorageError(
          "Failed to move data",
          StorageErrorCode.OPERATION_FAILED,
          { error },
        ),
      };
    }
  }

  /**
   * Select appropriate storage for data
   */
  private async selectStorage(
    data: unknown,
    _options?: StorageOptions,
  ): Promise<StorageLayer> {
    try {
      const size = this.calculateSize(data);
      const status = await this.getStatus();

      // Use localStorage for small data if available
      if (
        size <= this.LOCAL_STORAGE_SIZE_LIMIT &&
        status.localStorage.available
      ) {
        return this.storages.get("localStorage")!;
      }

      // Use IndexedDB for larger data
      if (status.indexedDB.available) {
        return this.storages.get("indexedDB")!;
      }

      // Fallback to localStorage if IndexedDB not available
      if (status.localStorage.available) {
        return this.storages.get("localStorage")!;
      }

      throw new StorageError(
        "No storage available",
        StorageErrorCode.NOT_AVAILABLE,
      );
    } catch (error) {
      logger.error("Failed to select storage", {
        error: error instanceof Error ? error : new Error(String(error)),
      });

      // Default to localStorage
      return this.storages.get("localStorage")!;
    }
  }

  /**
   * Calculate size of data in bytes
   */
  private calculateSize(data: unknown): number {
    try {
      const str = JSON.stringify(data);
      return str.length * 2; // Unicode characters
    } catch (error) {
      logger.warn("Failed to calculate data size", {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      return Infinity; // Force IndexedDB for data we can't measure
    }
  }
}

// Export singleton instance
export const storageCoordinator = new StorageCoordinator();
